import { html, TemplateResult } from 'lit';
import { OutlineElement } from '../../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-graphs-sectors.css.lit';
import '../../idb-graphs-no-data/idb-graph-no-data';

interface BarItemList {
  graphText: string;
  graphNumber: number;
  barWidth: number;
}

interface BreakdownObj {
  tid: string;
  name: string;
  TOTAL: number;
  SUM: number;
}

interface JsonObject {
  id: string;
  filter_a: string;
  filter_b: string;
  total_projects: number;
  total_amount: number;
  breakdown_a: BreakdownObj[];
  breakdown_b: BreakdownObj[];
}

interface jsonData {
  [key: string]: JsonObject;
}

interface nameObject {
  id: string;
  label: string;
}

/**The IDB Graphs Sectors component.
 * @element idb-graphs-sectors
 */
@customElement('idb-graphs-sectors')
export class IDBGraphsSectors extends OutlineElement {
  static styles = [componentStyles];

  /**
   * The title.
   */
  @property({ type: String, attribute: 'graphs-title' })
  graphsTitle: string;

  /**
   * The loans title.
   */
  @property({ type: String, attribute: 'loans-txt' })
  loansTxt: string;

  /**
   * Names for buttons.
   */
  @property({ type: Object, attribute: 'buttons' })
  buttons: string;

  /**
   * Names for radios.
   */
  @property({ type: Object, attribute: 'radios' })
  radios: string;

  /**
   * Names for tabs.
   */
  @property({ type: Object, attribute: 'tabs' })
  tabs: string;

  /**
   * Names for stat bar.
   */
  @property({ type: Object, attribute: 'stat-bar-label' })
  statBarLabel: string;

  /**
   * Data coming from the API.
   */
  @property({ type: Object, attribute: 'data-list' })
  dataList: jsonData;

  /**
   * Data formatted for the title.
   */
  @property({ type: Object })
  statsData: object;

  /**
   * Data list for country bars
   */
  @property({ type: Array })
  barCountryItemList: BarItemList[];

  /**
   * Data list for sector bars
   */
  @property({ type: Array })
  barSectorItemList: BarItemList[];

  /**
   * Templates for Bars.
   */
  pieChart: TemplateResult<1>;
  sectorBars: TemplateResult<1>;

  /**
   * Names and IDs for radio buttons.
   */
  @property({ type: Array })
  buttonValues: nameObject[];

  /**
   * Names and IDs for radio buttons.
   */
  @property({ type: Array })
  radioValues: nameObject[];

  /**
   * Data switcher.
   */
  @property({ type: String })
  dataSwitcher: string;

  /**
   * Button switcher.
   */
  @property({ type: String })
  buttonSwitcher: string;

  /**
   * Radio switcher.
   */
  @property({ type: String })
  radioSwitcher: string;

  connectedCallback() {
    super.connectedCallback();
    this.setData();
  }

  constructor() {
    super();
    this.barCountryItemList = [];
    this.barSectorItemList = [];
    this.statsData = [];
    this.buttonSwitcher = '5';
    this.radioSwitcher = 'SG';
    this.dataSwitcher = `${this.radioSwitcher}${this.buttonSwitcher}`;
  }

  render(): TemplateResult {
    return html`
      <div class="idb-graphs-sectors">
        <div class="idb-graphs-sectors--header">
          <idb-heading level="h2" level-size="xl" level-style="semibold" capital-style='uppercase'>
            ${this.graphsTitle}
          </idb-heading>
        </div>

        <div class="idb-graphs-sectors--action-wrapper">
          ${this.headerTabs()}
        </div>
        
        <idb-stat-bar 
          component-color="blue-white" 
          style="small" 
          stats=${JSON.stringify(this.statsData)}
        >
        </idb-stat-bar>

        ${this.renderContent()}
      </div>
    `;
  }

  headerTabs(): TemplateResult {
    return html`
      <idb-card-grid row-background-color="transparent" gap-size="small" columns-lg="4" columns-md="4" columns-sm="1" columns-default="1">
        ${this.buttonValues.map((value, index) => {
          return html`
            <idb-button 
              id="${value.id}"
              style="height: 100%;"
              center-items
              button-variant="quarter"
              button-size="large"
              ?active=${index == 2}
              @click=${this._handleClickButtons}
              @keydown=${this._handleKeyDown}
            >
              ${value.label}
            </idb-button>`;
        })}
      </idb-card-grid>
        <div class="fieldset-wrapper">
          ${this.radioValues.map((value, index) => {
            return html`
              <div class="form-item form-item-display-as">
                <input 
                  type="radio"
                  id="display-as-${index + 1}""
                  name="display_as"
                  value="${value.id}"
                  class="form-radio"
                  ?checked=${index == 0}
                  @click=${this._focusHandler}
                >
                <label for="display-as-${index + 1}" class="font-body option">
                  ${value.label}
                </label>
              </div>`;
          })}
        </div>`;
  }

  renderContent(): TemplateResult {
    const pieChartData = this.dataList[this.dataSwitcher];
    const totalAmount: string = this.getTotalAmount();

    return html`
      ${
        totalAmount != '0'
          ? html`
        <div class="idb-graphs-sectors--graphs-wrapper">
          <div class="graphs">
            <idb-heading level="h3" level-style="semibold" slot="heading" level-size="lg" margin-size="0px">
              ${this.tabs[0]}
            </idb-heading>
            <idb-pie-chart data="${JSON.stringify(pieChartData)}">
            </idb-pie-chart>
          </div>

          <div class="graphs">
            <idb-heading level="h3" level-style="semibold" slot="heading" level-size="lg" margin-size="0px">
              ${this.tabs[1]}
            </idb-heading>
            <div class="stat-wrapper">
              <p class="stat stat--desc">${this.loansTxt}</p>
              <p class="stat"><span>$</span> ${totalAmount}</>
            </div>
            <idb-bar-graphs
              item-list=${JSON.stringify(this.barSectorItemList)}
            >
            </idb-bar-graphs>
          </div>
        </div>`
          : html`
          <idb-graph-no-data>
            <p slot="no-data-message">No data for the selected combination</p>
          </idb-graph-no-data>`
      }
    `;
  }

  _handleClickButtons(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const id = target.getAttribute('id') ?? '';
    this.buttonSwitcher = id;
    this.setDataSwitcher();
    this.activateButtons(id);
  }

  _handleKeyDown(event: KeyboardEvent) {
    // Activate button.
    if (['Enter', ' '].includes(event.key)) {
      const target = event.target as HTMLElement;
      const id = target.getAttribute('id') ?? '';
      this.buttonSwitcher = id;
      this.setDataSwitcher();
      this.activateButtons(id);
    }
  }

  _focusHandler(event: Event) {
    const target = event.target as HTMLElement;
    const value = target.getAttribute('value') ?? '';
    this.radioSwitcher = value;
    this.setDataSwitcher();
  }

  activateButtons(id: string) {
    const buttons = this.shadowRoot?.querySelectorAll('idb-button');
    buttons?.forEach(button => {
      // Add class to all buttons except to the clicked one.
      if (button.id !== id) {
        button.removeAttribute('active');
      } else {
        button.setAttribute('active', 'active');
      }
    });
  }

  setDataSwitcher() {
    this.dataSwitcher = `${this.radioSwitcher}${this.buttonSwitcher}`;
    this.setData();
  }

  setData = () => {
    this.buttonValues = [
      {
        id: '0',
        label: this.buttons[0],
      },
      {
        id: '1',
        label: this.buttons[1],
      },
      {
        id: '5',
        label: this.buttons[2],
      },
      {
        id: 'ALL',
        label: this.buttons[3],
      },
    ];

    this.radioValues = [
      {
        id: 'SG',
        label: this.radios[0],
      },
      {
        id: 'NSG',
        label: this.radios[1],
      },
      {
        id: 'TC',
        label: this.radios[2],
      },
      {
        id: 'FSP',
        label: this.radios[3],
      },
    ];

    const totalAmount: string = this.getTotalAmount();
    const totalApproved = `USD ${totalAmount}`;
    const totalProjects: number =
      this.dataList[this.dataSwitcher]?.total_projects ?? 0;

    this.statsData = [
      {
        stat: totalProjects,
        desc: this.statBarLabel[0],
      },
      {
        stat: totalApproved,
        desc: this.statBarLabel[1],
      },
    ];

    const dataPerCountry = this.dataList[this.dataSwitcher]?.breakdown_a ?? [];
    this.barCountryItemList = [];
    for (const [, value] of Object.entries(dataPerCountry)) {
      const barWidth: number =
        (value.TOTAL * 100) /
        this.dataList[this.dataSwitcher]['breakdown_a'][0]['TOTAL'];

      this.barCountryItemList.push({
        graphText: value.name,
        graphNumber: value.TOTAL,
        barWidth: barWidth,
      });
    }

    const dataPerSector = this.dataList[this.dataSwitcher]?.breakdown_b ?? [];
    this.barSectorItemList = [];
    for (const [, value] of Object.entries(dataPerSector)) {
      const barWidth: number =
        (value.TOTAL * 100) /
        this.dataList[this.dataSwitcher]['breakdown_b'][0]['TOTAL'];

      this.barSectorItemList.push({
        graphText: value.name,
        graphNumber: value.TOTAL,
        barWidth: barWidth,
      });
    }

    this.requestUpdate();
  };

  getTotalAmount = () => {
    const totalAmount: number =
      this.dataList[this.dataSwitcher]?.total_amount ?? 0;
    let amount = '0';
    if (totalAmount > 0) {
      if (totalAmount > 1000) {
        amount = (totalAmount / 1000).toFixed(2);
        amount = `${amount} B`;
      } else {
        amount = totalAmount.toFixed(2);
        amount = `${amount} M`;
      }
    }
    return amount;
  };
}
